import { AavoFormLayout, AavoFormLayoutProps } from "src/components/common/forms/AavoFormLayout.tsx";
import React from "react";
import { FieldValues, UseFormProps } from "react-hook-form";
import { Box } from "@mui/material";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AavoFormFooter } from "src/components/common/forms/AavoFormFooter.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import i18n from "i18next";
import { useAavoForm } from "src/components/common/forms/useAavoForm/useAavoForm.ts";
import {
	AavoFormOnSubmitFunc,
	UseAavoFormParams,
	UseAavoFormReturn,
} from "src/components/common/forms/useAavoForm/types.ts";
import type { AgentFormContext } from "src/components/views/ai/assistant/uiContext/agentUiNodeTypes.ts";
import { AgentFormUiContext } from "src/components/views/ai/assistant/uiContext/AgentFormUiContext.tsx";

export interface AavoFormProps<TFieldValues extends FieldValues, TResult>
	extends Pick<UseAavoFormParams<TFieldValues, TResult>, "onCompleted" | "onFormEdited" | "defaultValues">,
		Omit<AavoFormLayoutProps, "children"> {
	submit: UseAavoFormParams<TFieldValues, TResult>["onSubmit"];
	render: (props: AavoFormContentParams<TFieldValues>) => React.ReactNode;
	useFormProps?: Omit<UseFormProps<TFieldValues>, "defaultValues">;
	submitLabel?: string;
	cancelLabel?: string;
	disableSubmit?: boolean;
	hideSubmit?: boolean;
	hideCancel?: boolean;
	hideActions?: boolean;
	footerExtraComponents?: (
		formContentParams: AavoFormContentParams<TFieldValues>,
		triggerSubmit: (submitFunc?: AavoFormOnSubmitFunc<TFieldValues, TResult>) => Promise<void>,
	) => React.ReactNode;
	footerStartComponents?: React.ReactNode;
	noLayout?: boolean;
	submitOnEnter?: boolean;
	agentContext: AgentFormContext<TFieldValues>;
}

export interface AavoFormContentParams<TFieldValues extends FieldValues>
	extends Omit<UseAavoFormReturn<TFieldValues, unknown>, "triggerSubmit"> {
	triggerSubmit: () => Promise<void>;
}

export const AavoForm = <TFieldValues extends FieldValues, TResult = void>({
	render,
	defaultValues,
	useFormProps,
	submit,
	disableSubmit,
	onCompleted,
	onFormEdited,
	columnMinWidth,
	columns = 1,
	fitContent,
	layoutSx,
	submitLabel,
	cancelLabel,
	hideCancel = false,
	hideSubmit = false,
	hideActions = false,
	footerExtraComponents,
	footerStartComponents,
	noLayout = false,
	submitOnEnter = false,
	agentContext,
}: AavoFormProps<TFieldValues, TResult>) => {
	const useAavoFormReturn = useAavoForm<TFieldValues, TResult>({
		defaultValues: defaultValues,
		onSubmit: submit,
		onCompleted: onCompleted,
		onFormEdited: onFormEdited,
		...useFormProps,
	});

	const aavoFormContentParams: AavoFormContentParams<TFieldValues> = {
		...useAavoFormReturn,
		triggerSubmit: () => useAavoFormReturn.triggerSubmit(),
	};

	return (
		<AgentFormUiContext agentContext={agentContext} form={useAavoFormReturn}>
			<VerticalBox
				sx={{
					flex: 1,
				}}
				onKeyDown={onFormKeyDown}
			>
				{noLayout ?
					render(aavoFormContentParams)
				:	<AavoFormLayout
						columns={columns}
						columnMinWidth={columnMinWidth}
						fitContent={fitContent}
						layoutSx={layoutSx}
					>
						{render(aavoFormContentParams)}
					</AavoFormLayout>
				}
				{!hideActions && (
					<AavoFormFooter>
						{footerStartComponents}
						<Box sx={{ flex: 1 }} />
						{!hideCancel && (
							<AsyncButton
								label={cancelLabel ?? i18n.t("close")}
								color={"secondary"}
								onClick={() => useAavoFormReturn.triggerCancel()}
							/>
						)}
						{!hideSubmit && (
							<AsyncButton
								label={submitLabel ?? i18n.t("save")}
								tooltip={i18n.t("save_shortcut_hint")}
								disabled={disableSubmit || useAavoFormReturn.formState.disabled}
								onClick={() => useAavoFormReturn.triggerSubmit()}
							/>
						)}
						{footerExtraComponents?.(aavoFormContentParams, useAavoFormReturn.triggerSubmit)}
					</AavoFormFooter>
				)}
			</VerticalBox>
		</AgentFormUiContext>
	);

	async function onFormKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
		if (e.key === "Enter") {
			if (submitOnEnter || e.ctrlKey) {
				await useAavoFormReturn.triggerSubmit();
			}
		}
	}
};
